import React, {useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Loading from '../../components/Loading/Loading';
import {VulnerabilityFragment} from '../../schema-types/VulnerabilityFragment';
import {useContactVulnerabilities} from './useContactVulnerabilities';
import {VulnerabilityOptionLists} from './useVulnerabilityOptions';
import {VulnerabilityModal, VulnerabilityModalContact} from './VulnerabilityModal';
import {formatVulnerabilityDate} from './vulnerabilityFormatting';

type Props = {
  accountId: string;
  contact: VulnerabilityModalContact;
  options: VulnerabilityOptionLists;
  /** Hide the add and edit controls, for users without edit rights. */
  readOnly?: boolean;
  /** Omit the contact name heading when the surrounding panel already names the contact. */
  hideContactName?: boolean;
};

/**
 * SPP account overview: one contact's active vulnerabilities as Curo holds
 * them, with add and edit via `VulnerabilityModal`. Each save syncs to Curo at
 * once, so the table is refetched (with a Curo pull) after every save.
 */
export const ContactVulnerabilitiesTable: React.FC<Props> = ({
  accountId,
  contact,
  options,
  readOnly = false,
  hideContactName = false,
}) => {
  const {existing, loading, refetch} = useContactVulnerabilities(contact.id, {
    curoSync: true,
  });
  const [modal, setModal] = useState<{open: boolean; row: VulnerabilityFragment | null}>({
    open: false,
    row: null,
  });
  const contactName = contact.simplifiedName || 'Contact';

  return (
    <Box mb={3} data-testid={`contact-vulnerabilities-${contact.id}`}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        {hideContactName ? null : <Typography variant="h6">{contactName}</Typography>}
        {readOnly ? null : (
          <IconButton
            size="small"
            color="primary"
            aria-label={`Add vulnerability for ${contactName}`}
            onClick={() => setModal({open: true, row: null})}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      {loading ? (
        <Loading entityName="vulnerabilities" />
      ) : existing.length === 0 ? (
        <Typography color="text.secondary">No vulnerabilities recorded</Typography>
      ) : (
        <Table size="small" aria-label={`${contactName} vulnerabilities`}>
          <TableHead>
            <TableRow>
              <TableCell>Vulnerability</TableCell>
              <TableCell>Service Level</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {existing.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.vulnerabilityType}</TableCell>
                <TableCell>{row.serviceLevel}</TableCell>
                <TableCell>{formatVulnerabilityDate(row.startDate)}</TableCell>
                <TableCell>{formatVulnerabilityDate(row.endDate)}</TableCell>
                <TableCell align="right">
                  {readOnly ? null : (
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={`Edit vulnerability ${row.vulnerabilityType}`}
                      onClick={() => setModal({open: true, row})}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {modal.open ? (
        <VulnerabilityModal
          open
          onClose={() => setModal({open: false, row: null})}
          onSaved={() => {
            refetch();
          }}
          accountId={accountId}
          contact={contact}
          existing={modal.row}
          options={options}
        />
      ) : null}
    </Box>
  );
};
